import { useState, useMemo, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Reservation, AfipVoucher, ServiceBooking } from '@turizi/shared';
import {
    Tooltip,
    ActionIcon,
    Button,
    SegmentedControl,
    Select,
    NumberInput,
    TextInput,
    Checkbox,
    FileInput,
    Loader,
} from '@mantine/core';
import CardModal from '@/components/card-modal';
import {
    IconReceipt,
    IconDownload,
    IconMail,
    IconPlus,
    IconExternalLink,
    IconTrash,
    IconUpload,
} from '@tabler/icons-react';
import { formatPrice, formatDayShort } from '@/lib/format-utils';
import { notify } from '@/lib/notify';
import SendAfipVoucherEmailModal from '@/components/send-afip-voucher-email-modal';
import { MoneyNumberInput } from '@/components/snippets';
import CurrencySelector from '@/components/currency-selector';
import {
    useCreateReceivedAfipVoucher,
    useGetAfipVouchers,
    useLinkVoucherToServiceBooking,
    useUnlinkVoucherFromServiceBooking,
} from '@/hooks/api-hooks';
import api from '@/services/api-service';

interface ReservationAfipVouchersProps {
    reservation: Reservation;
    showEmail?: boolean;
}

const VOUCHER_TYPE_OPTIONS = [
    { value: '1', label: 'Factura A' },
    { value: '6', label: 'Factura B' },
    { value: '11', label: 'Factura C' },
    { value: '2', label: 'Nota de Débito A' },
    { value: '3', label: 'Nota de Crédito A' },
    { value: '7', label: 'Nota de Débito B' },
    { value: '8', label: 'Nota de Crédito B' },
    { value: '12', label: 'Nota de Débito C' },
    { value: '13', label: 'Nota de Crédito C' },
];

export default function ReservationAfipVouchers({
    reservation,
    showEmail = true
}: ReservationAfipVouchersProps) {
    const { t } = useTranslation();
    const afipVouchers: AfipVoucher[] = Array.isArray(reservation?.afip_vouchers) ? reservation.afip_vouchers : [];
    const bookings: ServiceBooking[] = Array.isArray(reservation?.service_bookings) ? reservation.service_bookings : [];

    // Aggregate all received provider vouchers linked across any service_booking of this reservation
    const receivedVouchersWithBookings = useMemo(() => {
        const map = new Map<number, { voucher: AfipVoucher; bookings: ServiceBooking[] }>();
        bookings.forEach((b) => {
            const vList: AfipVoucher[] = Array.isArray(b.afip_vouchers) ? b.afip_vouchers : [];
            vList.forEach((v) => {
                if (!map.has(v.id)) {
                    map.set(v.id, { voucher: v, bookings: [b] });
                } else {
                    map.get(v.id)!.bookings.push(b);
                }
            });
        });
        return Array.from(map.values());
    }, [bookings]);

    const [downloadingVoucherIds, setDownloadingVoucherIds] = useState<Record<number, boolean>>({});
    const [selectedVoucherForEmail, setSelectedVoucherForEmail] = useState<AfipVoucher | null>(null);

    // Provider voucher modal states
    const [modalOpened, setModalOpened] = useState(false);
    const [mode, setMode] = useState<'new' | 'existing'>('new');
    const [selectedBookingIds, setSelectedBookingIds] = useState<number[]>([]);
    const [cbteTipo, setCbteTipo] = useState('1');
    const [pos, setPos] = useState<number | string>(1);
    const [voucherNumber, setVoucherNumber] = useState<number | string>('');
    const [issueDate, setIssueDate] = useState<string>(() => new Date().toISOString().split('T')[0]);
    const [currency, setCurrency] = useState<string>('ARS');
    const [impTotal, setImpTotal] = useState<number>(0);
    const [cae, setCae] = useState('');
    const [caeExpiration, setCaeExpiration] = useState('');
    const [issuerName, setIssuerName] = useState('');
    const [issuerCuit, setIssuerCuit] = useState('');
    const [issuerBusinessId, setIssuerBusinessId] = useState<number | null>(null);
    const [attachments, setAttachments] = useState<string[]>([]);
    const [isUploading, setIsUploading] = useState(false);
    const [selectedExistingVoucherId, setSelectedExistingVoucherId] = useState<string | null>(null);

    const createReceivedMutation = useCreateReceivedAfipVoucher();
    const linkMutation = useLinkVoucherToServiceBooking();
    const unlinkMutation = useUnlinkVoucherFromServiceBooking();

    const { data: allReceivedVouchers } = useGetAfipVouchers(
        {
            direction: 'received',
            issuer_business_id: issuerBusinessId || undefined,
        },
        { enabled: modalOpened && mode === 'existing' }
    );

    useEffect(() => {
        if (modalOpened) {
            const defaultBooking = bookings.find((b) => Number(b.money_out_amount || 0) > 0) || bookings[0];
            if (defaultBooking) {
                setSelectedBookingIds([Number(defaultBooking.id)]);
                setImpTotal(Number(defaultBooking.money_out_amount || 0));
                setCurrency(defaultBooking.money_out_currency || 'ARS');
                const prov = defaultBooking.selected_provider;
                setIssuerBusinessId(defaultBooking.selected_provider_id || prov?.id || null);
                setIssuerName(prov?.name || '');
                setIssuerCuit(prov?.tax_id_number || '');
            } else {
                setSelectedBookingIds([]);
                setImpTotal(0);
            }
            setSelectedExistingVoucherId(null);
        }
    }, [modalOpened]);

    const handleToggleBookingSelection = (booking: ServiceBooking, checked: boolean) => {
        const bId = Number(booking.id);
        const nextIds = checked
            ? [...selectedBookingIds, bId]
            : selectedBookingIds.filter((id) => id !== bId);
        setSelectedBookingIds(nextIds);

        // Update suggested total & provider info from selected bookings
        const selectedList = bookings.filter((b) => nextIds.includes(Number(b.id)));
        if (selectedList.length > 0) {
            const sumCost = selectedList.reduce((acc, b) => acc + Number(b.money_out_amount || 0), 0);
            if (sumCost > 0) setImpTotal(sumCost);
            const firstWithProv = selectedList.find((b) => b.selected_provider || b.selected_provider_id);
            if (firstWithProv) {
                setIssuerBusinessId(firstWithProv.selected_provider_id || firstWithProv.selected_provider?.id || null);
                setIssuerName(firstWithProv.selected_provider?.name || '');
                setIssuerCuit(firstWithProv.selected_provider?.tax_id_number || '');
                if (firstWithProv.money_out_currency) {
                    setCurrency(firstWithProv.money_out_currency);
                }
            }
        }
    };

    const handleUploadFile = async (file: File | null) => {
        if (!file) return;
        setIsUploading(true);
        try {
            const formData = new FormData();
            formData.append('image', file);
            const res = await api.upload<{ url: string; key: string }>('/payments/upload-attachment', formData);
            if (res.error) {
                notify.error(t('error'), typeof res.error === 'string' ? res.error : t('error'));
            } else if (res.data?.url) {
                setAttachments((prev) => [...prev, res.data!.url]);
            }
        } catch {
            notify.error(t('error'), t('error'));
        } finally {
            setIsUploading(false);
        }
    };

    const handleCreateAndLink = () => {
        if (selectedBookingIds.length === 0) {
            notify.error(t('error'), t('select_at_least_one_booking'));
            return;
        }
        if (!pos || !voucherNumber || impTotal <= 0) return;

        createReceivedMutation.mutate(
            {
                issuer_business_id: issuerBusinessId,
                issuer_name: issuerName,
                issuer_cuit: issuerCuit,
                issue_date: issueDate || null,
                currency,
                attachments,
                pos: Number(pos),
                cbte_tipo: Number(cbteTipo),
                voucher_number: Number(voucherNumber),
                imp_total: impTotal,
                imp_neto: impTotal,
                imp_iva: 0,
                cae: cae.trim(),
                cae_expiration: caeExpiration.trim(),
                service_booking_ids: selectedBookingIds,
            },
            {
                onSuccess: () => {
                    notify.success(t('provider_voucher_created'), t('success'));
                    setModalOpened(false);
                    setVoucherNumber('');
                    setCae('');
                    setCaeExpiration('');
                    setAttachments([]);
                },
            }
        );
    };

    const handleLinkExisting = async () => {
        if (selectedBookingIds.length === 0 || !selectedExistingVoucherId) return;
        const vId = Number(selectedExistingVoucherId);
        try {
            for (const bId of selectedBookingIds) {
                await linkMutation.mutateAsync({
                    serviceBookingId: bId,
                    voucherId: vId,
                });
            }
            notify.success(t('provider_voucher_linked'), t('success'));
            setModalOpened(false);
        } catch {
            notify.error(t('error'), t('error'));
        }
    };

    const handleUnlinkFromBookings = async (voucherId: number, linkedBookings: ServiceBooking[]) => {
        try {
            for (const b of linkedBookings) {
                if (b.id) {
                    await unlinkMutation.mutateAsync({
                        serviceBookingId: b.id,
                        voucherId,
                    });
                }
            }
            notify.success(t('provider_voucher_unlinked'), t('success'));
        } catch {
            notify.error(t('error'), t('error'));
        }
    };

    const handleDownloadVoucher = async (voucher: AfipVoucher) => {
        setDownloadingVoucherIds(prev => ({ ...prev, [voucher.id]: true }));
        try {
            const fileName = `${t('receipt_number')}_${reservation?.client_business?.name || ''}-${t('reservation')}-#${reservation.id}_${voucher.voucher_number}.pdf`;

            const response = await fetch(`${import.meta.env.VITE_API_BASE_URL}/afip/vouchers/${voucher.id}/download?filename=${encodeURIComponent(fileName)}`, {
                method: 'GET',
                credentials: 'include'
            });

            if (!response.ok) {
                throw new Error('Failed to download PDF');
            }

            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = fileName;
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(url);
        } catch {
            notify.error('error', 'failed_to_download_pdf');
        } finally {
            setDownloadingVoucherIds(prev => ({ ...prev, [voucher.id]: false }));
        }
    };

    return (
        <>
            <div className="data-card space-y-3">
                {/* Emitted Sales Vouchers */}
                {afipVouchers.length > 0 && (
                    <div className="space-y-2">
                        <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider block">
                            {t('afip_vouchers')} ({t('emitted_vouchers')})
                        </span>
                        <div className="space-y-1.5">
                            {afipVouchers.map((voucher) => {
                                const formatted = `${String(voucher.pos).padStart(4, '0')}-${String(voucher.voucher_number).padStart(8, '0')}`;
                                const isDownloading = downloadingVoucherIds[voucher.id];
                                return (
                                    <div
                                        key={`voucher-${voucher.id}`}
                                        className="flex items-center justify-between p-2 rounded-lg border border-gray-100 bg-gray-50/70 hover:bg-gray-50 transition-colors"
                                    >
                                        <div className="min-w-0 pr-2">
                                            <div className="flex items-center gap-1.5">
                                                <IconReceipt size={14} className="text-blue-500 shrink-0" />
                                                <span className="text-xs font-semibold text-slate-800 font-mono">
                                                    {formatted}
                                                </span>
                                            </div>
                                            <div className="text-[11px] text-gray-500 mt-0.5">
                                                <span>{t('taxed_amount')}: </span>
                                                <span className="font-semibold text-slate-700">
                                                    ${formatPrice(voucher.imp_total)}
                                                </span>
                                            </div>
                                        </div>
                                        <div className="flex items-center gap-1 shrink-0">
                                            <Tooltip label={t('download_pdf')} withArrow>
                                                <ActionIcon
                                                    variant="light"
                                                    color="gray"
                                                    size="sm"
                                                    onClick={() => handleDownloadVoucher(voucher)}
                                                    loading={isDownloading}
                                                >
                                                    <IconDownload size={15} />
                                                </ActionIcon>
                                            </Tooltip>
                                            {showEmail && (
                                                <Tooltip label={t('send_by_email')} withArrow>
                                                    <ActionIcon
                                                        variant="light"
                                                        color="blue"
                                                        size="sm"
                                                        onClick={() => {
                                                            setSelectedVoucherForEmail(voucher);
                                                        }}
                                                    >
                                                        <IconMail size={15} />
                                                    </ActionIcon>
                                                </Tooltip>
                                            )}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                )}

                {/* Received Provider Vouchers */}
                <div className={afipVouchers.length > 0 ? 'pt-2 border-t border-gray-100 space-y-2' : 'space-y-2'}>
                    <div className="flex items-center justify-between gap-2">
                        <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">
                            {t('provider_tax_vouchers')}
                        </span>
                        {bookings.length > 0 && (
                            <Button
                                size="compact-xs"
                                variant="light"
                                color="dark"
                                leftSection={<IconPlus size={12} />}
                                onClick={() => setModalOpened(true)}
                            >
                                {t('add')}
                            </Button>
                        )}
                    </div>

                    {receivedVouchersWithBookings.length === 0 ? (
                        <div className="text-[11px] text-slate-400 italic">
                            {t('no_provider_vouchers')}
                        </div>
                    ) : (
                        <div className="space-y-1.5">
                            {receivedVouchersWithBookings.map(({ voucher, bookings: linkedBookings }) => {
                                const formatted = `${String(voucher.pos).padStart(4, '0')}-${String(voucher.voucher_number).padStart(8, '0')}`;
                                const firstAttachment = Array.isArray(voucher.attachments) && voucher.attachments.length > 0
                                    ? voucher.attachments[0]
                                    : null;
                                return (
                                    <div
                                        key={`rec-voucher-${voucher.id}`}
                                        className="flex items-center justify-between p-2 rounded-lg border border-amber-200/70 bg-amber-50/30 text-xs"
                                    >
                                        <div className="min-w-0 pr-2">
                                            <div className="flex items-center gap-1.5 font-mono font-bold text-slate-800">
                                                <IconReceipt size={13} className="text-amber-600 shrink-0" />
                                                <span>{formatted}</span>
                                                {(voucher.issuer_business?.name || voucher.issuer_name) && (
                                                    <span className="font-sans font-normal text-[10px] text-slate-500 truncate">
                                                        ({voucher.issuer_business?.name || voucher.issuer_name})
                                                    </span>
                                                )}
                                            </div>
                                            <div className="text-[11px] text-slate-600 mt-0.5 flex items-center gap-1.5 flex-wrap">
                                                <span className="font-semibold">
                                                    ${formatPrice(Number(voucher.imp_total || 0))} {voucher.currency || 'ARS'}
                                                </span>
                                                <span className="text-[10px] text-slate-400">
                                                    ({linkedBookings.map((b) => `#${b.id}`).join(', ')})
                                                </span>
                                            </div>
                                        </div>
                                        <div className="flex items-center gap-1 shrink-0">
                                            {firstAttachment && (
                                                <Tooltip label={t('attachments')} withArrow>
                                                    <ActionIcon
                                                        size="sm"
                                                        variant="light"
                                                        color="blue"
                                                        component="a"
                                                        href={firstAttachment}
                                                        target="_blank"
                                                        rel="noreferrer"
                                                    >
                                                        <IconExternalLink size={14} />
                                                    </ActionIcon>
                                                </Tooltip>
                                            )}
                                            <ActionIcon
                                                size="sm"
                                                variant="subtle"
                                                color="red"
                                                loading={unlinkMutation.isPending}
                                                onClick={() => handleUnlinkFromBookings(voucher.id, linkedBookings)}
                                            >
                                                <IconTrash size={14} />
                                            </ActionIcon>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>
            </div>

            {/* Modal to Register or Link Provider Voucher across Reservation Service Bookings */}
            <CardModal
                opened={modalOpened}
                onClose={() => setModalOpened(false)}
                header={
                    <div className="font-bold text-sm flex items-center gap-1.5">
                        <IconReceipt size={16} />
                        <span>{t('add_provider_voucher')}</span>
                    </div>
                }
                size="full"
                footer={
                    <div className="flex justify-end gap-2">
                        <Button
                            size="xs"
                            variant="subtle"
                            color="gray"
                            onClick={() => setModalOpened(false)}
                        >
                            {t('cancel')}
                        </Button>
                        {mode === 'new' ? (
                            <Button
                                size="xs"
                                color="dark"
                                loading={createReceivedMutation.isPending}
                                // disabled={selectedBookingIds.length === 0 || !pos || !voucherNumber || impTotal <= 0}
                                onClick={handleCreateAndLink}
                            >
                                {t('save')}
                            </Button>
                        ) : (
                            <Button
                                size="xs"
                                color="dark"
                                loading={linkMutation.isPending}
                                // disabled={selectedBookingIds.length === 0 || !selectedExistingVoucherId}
                                onClick={handleLinkExisting}
                            >
                                {t('save')}
                            </Button>
                        )}
                    </div>
                }
            >
                <div className="space-y-4">
                    {/* Select which service booking(s) of this reservation are covered */}
                    <div className="space-y-1.5">
                        <div className="text-xs font-bold text-slate-700">
                            {t('select_service_bookings_for_voucher')}
                        </div>
                        <div className="max-h-40 overflow-y-auto space-y-1 p-2 rounded-lg bg-slate-50 border border-slate-200">
                            {bookings.map((b) => {
                                const checked = selectedBookingIds.includes(Number(b.id));
                                const provName = b.selected_provider?.name || '';
                                return (
                                    <Checkbox
                                        key={b.id}
                                        size="xs"
                                        checked={checked}
                                        onChange={(e) => handleToggleBookingSelection(b, e.currentTarget.checked)}
                                        label={
                                            <span className="text-xs text-slate-700">
                                                <span className="font-bold">#{b.id}</span>{' '}
                                                {b.service_data?.name ? `· ${b.service_data.name}` : ''}{' '}
                                                {provName ? `(${provName})` : ''}{' '}
                                                {b.start_day ? `· ${formatDayShort(b.start_day)}` : ''}{' '}
                                                — <span className="font-semibold">${formatPrice(Number(b.money_out_amount || 0))} {b.money_out_currency || 'ARS'}</span>
                                            </span>
                                        }
                                    />
                                );
                            })}
                        </div>
                    </div>

                    <SegmentedControl
                        fullWidth
                        size="xs"
                        value={mode}
                        onChange={(val) => setMode(val as 'new' | 'existing')}
                        data={[
                            { value: 'new', label: t('register_new_voucher') },
                            { value: 'existing', label: t('link_existing_voucher') },
                        ]}
                    />

                    {mode === 'new' ? (
                        <div className="space-y-3">
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                                <TextInput
                                    size="xs"
                                    label={t('issuer_provider')}
                                    value={issuerName}
                                    onChange={(e) => setIssuerName(e.currentTarget.value)}
                                />
                                <TextInput
                                    size="xs"
                                    label={t('issuer_cuit')}
                                    value={issuerCuit}
                                    onChange={(e) => setIssuerCuit(e.currentTarget.value)}
                                />
                            </div>

                            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2.5">
                                <Select
                                    size="xs"
                                    label={t('voucher_type_label')}
                                    data={VOUCHER_TYPE_OPTIONS}
                                    value={cbteTipo}
                                    onChange={(val) => setCbteTipo(val || '1')}
                                    allowDeselect={false}
                                />
                                <NumberInput
                                    size="xs"
                                    label={t('pos_label')}
                                    value={pos}
                                    onChange={(val) => setPos(val === '' ? '' : Number(val))}
                                    min={1}
                                    hideControls
                                />
                                <NumberInput
                                    size="xs"
                                    label={t('voucher_number_label')}
                                    value={voucherNumber}
                                    onChange={(val) => setVoucherNumber(val === '' ? '' : Number(val))}
                                    min={1}
                                    hideControls
                                />
                            </div>

                            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2.5 items-end">
                                <TextInput
                                    size="xs"
                                    type="date"
                                    label={t('issue_date')}
                                    value={issueDate}
                                    onChange={(e) => setIssueDate(e.currentTarget.value)}
                                />
                                <div>
                                    <CurrencySelector
                                        size="xs"
                                        label={t('currency')}
                                        value={currency}
                                        onChange={(val) => setCurrency(val || 'ARS')}
                                    />
                                </div>
                                <MoneyNumberInput
                                    size="sm"
                                    label={t('total_amount')}
                                    amount={impTotal}
                                    onChange={(val) => setImpTotal(Number(val || 0))}
                                />
                            </div>

                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                                <TextInput
                                    size="xs"
                                    label={t('cae_optional')}
                                    value={cae}
                                    onChange={(e) => setCae(e.currentTarget.value)}
                                />
                                <TextInput
                                    size="xs"
                                    type="date"
                                    label={t('cae_expiration_optional')}
                                    value={caeExpiration}
                                    onChange={(e) => setCaeExpiration(e.currentTarget.value)}
                                />
                            </div>

                            <div className="space-y-1.5">
                                <div className="flex items-center gap-2">
                                    {isUploading ? (
                                        <Loader size="xs" />
                                    ) : (
                                        <FileInput
                                            size="xs"
                                            className="flex-1"
                                            placeholder={t('upload_voucher_file')}
                                            accept="image/png,image/jpeg,application/pdf"
                                            leftSection={<IconUpload size={14} />}
                                            onChange={handleUploadFile}
                                        />
                                    )}
                                </div>
                                {attachments.length > 0 && (
                                    <div className="flex flex-wrap gap-1.5">
                                        {attachments.map((url, idx) => (
                                            <div
                                                key={idx}
                                                className="inline-flex items-center gap-1 text-xs bg-blue-50 text-blue-700 px-2 py-0.5 rounded border border-blue-200"
                                            >
                                                <a href={url} target="_blank" rel="noreferrer" className="hover:underline">
                                                    #{idx + 1}
                                                </a>
                                                <ActionIcon
                                                    size="xs"
                                                    color="blue"
                                                    variant="subtle"
                                                    onClick={() => setAttachments((prev) => prev.filter((_, i) => i !== idx))}
                                                >
                                                    <IconTrash size={11} />
                                                </ActionIcon>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>
                    ) : (
                        <div className="space-y-4">
                            {(allReceivedVouchers || []).length === 0 ? (
                                <div className="text-xs text-slate-400 italic py-4 text-center">
                                    {t('no_received_vouchers_for_provider')}
                                </div>
                            ) : (
                                <Select
                                    size="xs"
                                    label={t('select_existing_voucher')}
                                    data={(allReceivedVouchers || []).map((v) => ({
                                        value: String(v.id),
                                        label: `${String(v.pos).padStart(4, '0')}-${String(v.voucher_number).padStart(8, '0')} | $${formatPrice(Number(v.imp_total || 0))} ${v.currency || 'ARS'} (${v.issuer_business?.name || v.issuer_name || ''})`,
                                    }))}
                                    value={selectedExistingVoucherId}
                                    onChange={(val) => setSelectedExistingVoucherId(val ? String(val) : null)}
                                    searchable
                                />
                            )}
                        </div>
                    )}
                </div>
            </CardModal>

            {/* AFIP Voucher Email Modal */}
            {showEmail && (
                <SendAfipVoucherEmailModal
                    opened={!!selectedVoucherForEmail}
                    onClose={() => setSelectedVoucherForEmail(null)}
                    voucher={selectedVoucherForEmail}
                    business={reservation?.client_business}
                />
            )}
        </>
    );
}
